import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { clsx } from 'clsx';
import { ArrowLeft, AlertCircle, Loader2, GripVertical, Eye, MessageSquare, Save, Share2 } from 'lucide-react';
import ShareSettingsModal from '@/components/dashboard-builder/ShareSettingsModal';

import {
  DashboardBuildProvider,
  useDashboardBuild,
} from '@/contexts/DashboardBuildContext';
import BuildChatPanel from '@/components/dashboard-builder/BuildChatPanel';
import BuildCanvasPanel from '@/components/dashboard-builder/BuildCanvasPanel';

// ============================================================================
// Inner component that uses the context
// ============================================================================

const BuildModeContent: React.FC = () => {
  const { id: dashboardId } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { state, sendMessage, initDashboard, togglePreviewMode, saveDashboard } = useDashboardBuild();
  const [isSaving, setIsSaving] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  const initialPromptSent = useRef(false);

  // Panel resize state
  const [chatWidth, setChatWidth] = useState(400);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(0);
  const MIN_CHAT_WIDTH = 320;
  const MAX_CHAT_WIDTH = 700;

  // Initialize dashboard on mount
  useEffect(() => {
    if (dashboardId) {
      initDashboard(dashboardId);
    }
  }, [dashboardId, initDashboard]);

  // Send initial prompt from query params (from NewDashboardPage)
  useEffect(() => {
    if (
      initialPromptSent.current ||
      state.isLoading ||
      state.error
    )
      return;

    const prompt = searchParams.get('prompt');
    if (prompt) {
      initialPromptSent.current = true;
      // Clear the prompt from the URL to prevent re-sending on refresh
      setSearchParams({}, { replace: true });
      sendMessage(decodeURIComponent(prompt));
    }
  }, [state.isLoading, state.error, searchParams, setSearchParams, sendMessage]);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      await saveDashboard();
    } finally {
      setIsSaving(false);
    }
  }, [saveDashboard]);

  // Divider drag handlers
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      isDragging.current = true;
      startX.current = e.clientX;
      startWidth.current = chatWidth;
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (!isDragging.current) return;
        const delta = moveEvent.clientX - startX.current;
        const newWidth = Math.max(
          MIN_CHAT_WIDTH,
          Math.min(MAX_CHAT_WIDTH, startWidth.current + delta)
        );
        setChatWidth(newWidth);
      };

      const handleMouseUp = () => {
        isDragging.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [chatWidth]
  );

  // Loading state
  if (state.isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-primary animate-spin mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">
            Loading dashboard...
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (state.error) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="text-center max-w-md px-4">
          <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-foreground mb-2">
            Failed to load dashboard
          </h2>
          <p className="text-sm text-muted-foreground mb-4">
            {state.error}
          </p>
          <Link
            to="/dashboards"
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboards
          </Link>
        </div>
      </div>
    );
  }

  const isSaved = state.dashboard?.status === 'saved';

  return (
    <div className="flex flex-col h-screen bg-background">
      {/* Top bar */}
      <div className="flex-shrink-0 h-12 px-4 flex items-center gap-3 border-b border-border bg-background">
        <Link
          to="/dashboards"
          className="p-1.5 rounded-lg hover:bg-muted transition-colors"
        >
          <ArrowLeft className="w-4 h-4 text-muted-foreground" />
        </Link>
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <h1 className="text-sm font-semibold text-foreground truncate">
            {state.dashboard?.name || 'Dashboard'}
          </h1>
          <span
            className={clsx(
              'text-[10px] font-medium px-2 py-0.5 rounded-full flex-shrink-0',
              state.isPreviewMode
                ? 'bg-warn/15 text-warn'
                : 'bg-accent text-primary'
            )}
          >
            {state.isPreviewMode ? 'Preview Mode' : 'Build Mode'}
          </span>
          {isSaved && (
            <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-success/15 text-success flex-shrink-0">
              Saved
            </span>
          )}
        </div>

        {/* Top bar actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={togglePreviewMode}
            className={clsx(
              'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors',
              state.isPreviewMode
                ? 'bg-muted text-foreground hover:bg-accent'
                : 'bg-warn/15 text-warn hover:bg-warn/25'
            )}
          >
            {state.isPreviewMode ? (
              <>
                <MessageSquare className="w-3.5 h-3.5" />
                Back to Chat
              </>
            ) : (
              <>
                <Eye className="w-3.5 h-3.5" />
                Preview
              </>
            )}
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className={clsx(
              'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors',
              'bg-primary text-white hover:bg-primary',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            {isSaving ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Save className="w-3.5 h-3.5" />
            )}
            {isSaving ? 'Saving...' : 'Save'}
          </button>
          <button
            onClick={() => setShowShareModal(true)}
            disabled={!isSaved}
            className={clsx(
              'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors',
              isSaved
                ? 'text-muted-foreground hover:bg-accent'
                : 'text-muted-foreground cursor-not-allowed opacity-50'
            )}
            title={isSaved ? 'Share dashboard' : 'Save dashboard first to share'}
          >
            <Share2 className="w-3.5 h-3.5" />
            Share
          </button>
        </div>
      </div>

      {/* Split panels */}
      <div className="flex-1 flex overflow-hidden">
        {/* Chat panel — hidden in preview mode */}
        {!state.isPreviewMode && (
          <>
            <div style={{ width: chatWidth, flexShrink: 0 }}>
              <BuildChatPanel />
            </div>

            {/* Draggable divider */}
            <div
              onMouseDown={handleMouseDown}
              className={clsx(
                'w-2 flex-shrink-0 cursor-col-resize',
                'flex items-center justify-center',
                'bg-muted',
                'hover:bg-primary',
                'active:bg-primary/80 dark:active:bg-primary',
                'transition-colors'
              )}
            >
              <GripVertical className="w-3 h-3 text-muted-foreground" />
            </div>
          </>
        )}

        {/* Canvas panel */}
        <div className="flex-1 min-w-0">
          <BuildCanvasPanel />
        </div>
      </div>

      {dashboardId && state.dashboard && (
        <ShareSettingsModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          dashboardId={dashboardId}
          dashboardName={state.dashboard.name}
        />
      )}
    </div>
  );
};

// ============================================================================
// Page wrapper that provides context
// ============================================================================

const DashboardBuildPage: React.FC = () => {
  return (
    <DashboardBuildProvider>
      <BuildModeContent />
    </DashboardBuildProvider>
  );
};

export default DashboardBuildPage;
